import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import classes from "./NavigationProgressHandler.module.css";

type ProgressPhase = "idle" | "running" | "complete";

const COMPLETE_DURATION_MS = 300;

// The bar is animated with CSS instead of a ticking React store. Navigation
// runs in a React transition, and any React update while the transition is
// pending restarts its render, so the bar must not update React state while a
// navigation is in flight.
export function NavigationProgressHandler() {
	const location = useLocation();
	const [phase, setPhase] = useState<ProgressPhase>("idle");
	const lastPathname = useRef(location.pathname);

	// Handle anchor tag clicks
	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			if (isNavigationClick(event, lastPathname.current)) {
				setPhase("running");
			}
		};

		window.addEventListener("click", handleClick);
		return () => {
			window.removeEventListener("click", handleClick);
		};
	}, []);

	// Complete progress when location changes
	useEffect(() => {
		if (location.pathname === lastPathname.current) {
			return;
		}
		lastPathname.current = location.pathname;
		setPhase((current) => (current === "running" ? "complete" : current));
	}, [location.pathname]);

	useEffect(() => {
		if (phase !== "complete") {
			return;
		}
		const timeout = window.setTimeout(
			() => setPhase("idle"),
			COMPLETE_DURATION_MS,
		);
		return () => {
			window.clearTimeout(timeout);
		};
	}, [phase]);

	return (
		<div
			className={classes.bar}
			data-navigation-progress
			data-phase={phase}
			aria-hidden
		/>
	);
}

function isNavigationClick(event: MouseEvent, currentPathname: string) {
	const target = event.target as HTMLElement;
	const anchor = target.closest("a");

	return Boolean(
		anchor?.href &&
			!anchor.target &&
			anchor.href.startsWith(window.location.origin) &&
			// Ignore links that are the same route but with a hash
			!(anchor.pathname === currentPathname && anchor.hash !== "") &&
			// Ignore links that only change search parameters
			anchor.pathname !== currentPathname,
	);
}
